import { supabase } from '../supabaseClient/supabaseClient';
import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';

/**
 * Conversation Summary Service
 * Manages lightweight summaries of recent conversations per component
 * Follows ChatGPT's approach: title + user message snippets
 */
class ConversationSummaryService {
  constructor() {
    this.currentSessionMessages = new Map(); // componentType -> messages[]
    this.summariesCache = new Map(); // componentType -> summaries[]
  }

  /**
   * Add a message to current session
   * @param {string} componentType - 'task', 'reflection', 'course', 'project'
   * @param {string} role - 'user' or 'assistant'
   * @param {string} content - Message content
   * @param {object} metadata - Optional metadata
   */
  addMessage(componentType, role, content, metadata = {}) {
    if (!this.currentSessionMessages.has(componentType)) {
      this.currentSessionMessages.set(componentType, []);
    }

    this.currentSessionMessages.get(componentType).push({
      role,
      content,
      timestamp: new Date().toISOString(),
      metadata,
    });

    // Save to localStorage for persistence
    this.saveSessionToLocalStorage(componentType);
  }

  /**
   * Get current session messages for a component
   * @param {string} componentType
   */
  getCurrentSession(componentType) {
    return this.currentSessionMessages.get(componentType) || [];
  }

  /**
   * Generate and save summary for current session
   * @param {string} userId
   * @param {string} componentType
   * @param {string} componentId - Optional specific component ID
   * @param {string} sessionId
   */
  async generateAndSaveSummary(userId, componentType, componentId = null, sessionId) {
    const messages = this.getCurrentSession(componentType);
    if (messages.length === 0) return null;

    try {
      // Extract user messages only (following ChatGPT's approach)
      const userMessages = messages
        .filter((m) => m.role === 'user')
        .map((m) => m.content.substring(0, 200)); // Snippet only

      // Generate title using LLM
      const title = await this.generateTitle(messages, componentType);

      // Create summary object
      const summary = {
        user_id: userId,
        component_type: componentType,
        component_id: componentId,
        session_id: sessionId,
        timestamp: new Date().toISOString(),
        title,
        user_messages: userMessages,
        summary: {
          messageCount: messages.length,
          userMessageCount: userMessages.length,
          duration: this.calculateSessionDuration(messages),
        },
        metadata: this.extractMetadata(messages, componentType),
      };

      // Save to database
      const { data, error } = await supabase
        .from('ml_conversation_summaries')
        .insert(summary)
        .select()
        .single();

      if (error) throw error;

      // Update cache
      if (!this.summariesCache.has(componentType)) {
        this.summariesCache.set(componentType, []);
      }
      this.summariesCache.get(componentType).unshift(data);

      // Keep only last 15 summaries in cache
      if (this.summariesCache.get(componentType).length > 15) {
        this.summariesCache.get(componentType).pop();
      }

      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ConversationSummaryService] Error generating summary:', error);
      return null;
    }
  }

  /**
   * Generate a title for the conversation using LLM
   * @param {array} messages
   * @param {string} componentType
   */
  async generateTitle(messages, componentType) {
    try {
      // Use first few messages to generate title
      const contextMessages = messages.slice(0, 5);
      const conversationSnippet = contextMessages
        .map((m) => `${m.role}: ${m.content.substring(0, 100)}`)
        .join('\n');

      const response = await fetch(`${API_BASE_URL}/chat/generate-title`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify({
          conversation: conversationSnippet,
          componentType,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate title');
      }

      const data = await response.json();
      return data.title || this.generateFallbackTitle(messages, componentType);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ConversationSummaryService] Error generating title:', error);
      return this.generateFallbackTitle(messages, componentType);
    }
  }

  /**
   * Generate fallback title from first user message
   */
  generateFallbackTitle(messages, componentType) {
    const firstUserMessage = messages.find((m) => m.role === 'user');
    if (firstUserMessage) {
      const title = firstUserMessage.content.substring(0, 50);
      return title.length < firstUserMessage.content.length ? `${title}...` : title;
    }
    return `${componentType} conversation`;
  }

  /**
   * Load recent summaries for a component
   * @param {string} userId
   * @param {string} componentType
   * @param {number} limit - Number of summaries to load (default 15)
   */
  async loadRecentSummaries(userId, componentType, limit = 15) {
    try {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from('ml_conversation_summaries')
        .select('*')
        .eq('user_id', userId)
        .eq('component_type', componentType)
        .gte('timestamp', sevenDaysAgo)
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Update cache
      this.summariesCache.set(componentType, data || []);

      return data || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ConversationSummaryService] Error loading summaries:', error);
      return [];
    }
  }

  /**
   * Format summaries for LLM context
   * @param {string} componentType
   */
  formatForContext(componentType) {
    const summaries = this.summariesCache.get(componentType) || [];
    if (summaries.length === 0) return '';

    const formatted = summaries.map((summary) => {
      const timestamp = new Date(summary.timestamp).toLocaleString();
      const userSnippets = summary.user_messages.map((msg) => `|||| ${msg} ||||`).join('\n    ');

      return `${timestamp}: ${summary.title}\n    ${userSnippets}`;
    });

    return `**Recent Conversations (${componentType}):**\n${formatted.join('\n\n')}`;
  }

  /**
   * Extract metadata from messages based on component type
   */
  extractMetadata(messages, componentType) {
    const metadata = {
      totalMessages: messages.length,
      userMessages: messages.filter((m) => m.role === 'user').length,
      assistantMessages: messages.filter((m) => m.role === 'assistant').length,
    };

    // Component-specific metadata
    switch (componentType) {
      case 'task':
        metadata.taskActions = this.extractTaskActions(messages);
        break;
      case 'reflection':
        metadata.reflectionDepth = this.extractReflectionDepth(messages);
        break;
      case 'course':
        metadata.topicsDiscussed = this.extractTopics(messages);
        break;
      case 'project':
        metadata.projectPhase = this.extractProjectPhase(messages);
        break;
      default:
        break;
    }

    return metadata;
  }

  /**
   * Extract task-related actions from messages
   */
  extractTaskActions(messages) {
    const actions = [];
    const actionKeywords = ['create', 'update', 'complete', 'delete', 'schedule', 'prioritize'];

    messages.forEach((msg) => {
      if (msg.role === 'user') {
        const content = msg.content.toLowerCase();
        actionKeywords.forEach((keyword) => {
          if (content.includes(keyword)) {
            actions.push(keyword);
          }
        });
      }
    });

    return [...new Set(actions)]; // Remove duplicates
  }

  /**
   * Extract reflection depth indicators
   */
  extractReflectionDepth(messages) {
    const userMessages = messages.filter((m) => m.role === 'user');
    const avgLength =
      userMessages.reduce((sum, m) => sum + m.content.length, 0) / userMessages.length;

    if (avgLength > 500) return 'deep';
    if (avgLength > 200) return 'moderate';
    return 'brief';
  }

  /**
   * Extract topics discussed
   */
  extractTopics(messages) {
    // Simple keyword extraction (can be enhanced with NLP)
    const topics = new Set();
    const topicKeywords = [
      'algorithm',
      'data structure',
      'machine learning',
      'web development',
      'database',
      'api',
      'testing',
      'deployment',
    ];

    messages.forEach((msg) => {
      const content = msg.content.toLowerCase();
      topicKeywords.forEach((topic) => {
        if (content.includes(topic)) {
          topics.add(topic);
        }
      });
    });

    return Array.from(topics);
  }

  /**
   * Extract project phase
   */
  extractProjectPhase(messages) {
    const content = messages.map((m) => m.content.toLowerCase()).join(' ');

    if (content.includes('planning') || content.includes('design')) return 'planning';
    if (content.includes('implement') || content.includes('coding')) return 'implementation';
    if (content.includes('test') || content.includes('debug')) return 'testing';
    if (content.includes('deploy') || content.includes('launch')) return 'deployment';

    return 'unknown';
  }

  /**
   * Calculate session duration
   */
  calculateSessionDuration(messages) {
    if (messages.length < 2) return 0;

    const first = new Date(messages[0].timestamp).getTime();
    const last = new Date(messages[messages.length - 1].timestamp).getTime();

    return Math.floor((last - first) / 1000); // seconds
  }

  /**
   * Clear current session messages
   */
  clearSession(componentType) {
    this.currentSessionMessages.delete(componentType);
    localStorage.removeItem(`conversation_session_${componentType}`);
  }

  /**
   * Save session to localStorage
   */
  saveSessionToLocalStorage(componentType) {
    try {
      const messages = this.currentSessionMessages.get(componentType);
      localStorage.setItem(`conversation_session_${componentType}`, JSON.stringify(messages));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ConversationSummaryService] Error saving to localStorage:', error);
    }
  }

  /**
   * Load session from localStorage
   */
  loadSessionFromLocalStorage(componentType) {
    try {
      const cached = localStorage.getItem(`conversation_session_${componentType}`);
      if (cached) {
        this.currentSessionMessages.set(componentType, JSON.parse(cached));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ConversationSummaryService] Error loading from localStorage:', error);
    }
  }

  /**
   * Background sync - periodically save summaries
   * @param {string} userId
   * @param {string} componentType
   * @param {string} sessionId
   */
  async backgroundSync(userId, componentType, sessionId) {
    const messages = this.getCurrentSession(componentType);
    if (messages.length >= 5) {
      // Only sync if there are enough messages
      await this.generateAndSaveSummary(userId, componentType, null, sessionId);
    }
  }
}

// Singleton instance
const conversationSummaryService = new ConversationSummaryService();

export default conversationSummaryService;
